import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { fetchProduct } from '../utils/api';
import { useWishlist } from '../contexts/WishlistContext';
import ProductCard  from '../components/ProductCard';
import SkeletonGrid from '../components/SkeletonGrid';
import SEOHead from '../components/SEOHead';
import './Wishlist.css';

const Wishlist: React.FC = () => {
  const { ids } = useWishlist();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading,  setLoading]  = useState<boolean>(true);

  useEffect(() => {
    if (ids.length === 0) { setProducts([]); setLoading(false); return; }
    setLoading(true);
    Promise.allSettled(ids.map(id => fetchProduct(id)))
      .then(results => {
        const found = results
          .filter((r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof fetchProduct>>> => r.status === 'fulfilled')
          .map(r => r.value.product);
        // Preserve the order items were wishlisted in.
        const byId = new Map(found.map(p => [p.id, p]));
        setProducts(ids.map(id => byId.get(id)).filter((p): p is Product => !!p));
      })
      .finally(() => setLoading(false));
  }, [ids]);

  return (
    <div className="wishlist-page">
      <SEOHead title="My Wishlist" description="Products you've saved for later at Balaji Fashion House." url="/wishlist" />
      <div className="container">
        <div className="page-heading">
          <h1 className="section-title">My Wishlist</h1>
          <p className="section-subtitle">{ids.length > 0 ? `${ids.length} item${ids.length > 1 ? 's' : ''} saved` : 'Nothing saved yet'}</p>
        </div>

        {loading ? (
          <SkeletonGrid />
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🤍</div>
            <h3>Your wishlist is empty</h3>
            <p>Tap the heart icon on any product to save it here for later.</p>
            <Link to="/products" className="btn-primary" style={{ marginTop: '16px', display: 'inline-block' }}>
              Browse Collections
            </Link>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
